import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { injectedOmsApi } from '@/services/oms'
import { setClientCookie } from '@/utils/clientCookies'
import { injectedLocalApi } from '@/services/local'

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

type Nullable<T> = T | null

export type PlainInfo<T> = {
  status: RequestState
  error?: unknown
  data: Nullable<T>
  mode?: string
}

export interface MetaInitialState {
  listOfMenuSite: PlainInfo<Oms.ListOfMenuSite>
  detailMenuSite: PlainInfo<Oms.MetaDataMenuSite>
  isBot: boolean
  cartName: string | null
  trackers: PlainInfo<Oms.Tracker[]>
  scripts: PlainInfo<Oms.ShoppingCartTracker[]>
}

export const getMetaInitialState = (): MetaInitialState => ({
  listOfMenuSite: {
    status: 'none',
    data: null,
    error: null,
  },
  detailMenuSite: {
    status: 'none',
    data: null,
    error: null,
  },
  isBot: false,
  cartName: null,
  trackers: {
    status: 'none',
    data: null,
    error: null,
  },
  scripts: {
    status: 'none',
    data: [],
    error: null,
  },
})

export const metaSlice = createSlice({
  name: '@meta',
  initialState: getMetaInitialState(),
  reducers: {
    setListOfMenuSite: (
      state,
      action: PayloadAction<Oms.V4ApiResponse<Oms.ListOfMenuSite>>
    ) => {
      state.listOfMenuSite.status = 'fulfilled'
      state.listOfMenuSite.data = action.payload.data
    },
    setDetailMenuSite: (
      state,
      action: PayloadAction<Oms.V4ApiResponse<Oms.MetaDataMenuSite>>
    ) => {
      if (action.payload.errors) {
        state.detailMenuSite.status = 'rejected'
        return
      }
      state.detailMenuSite = {
        ...action.payload,
        status: 'fulfilled',
        data: {
          ...state.detailMenuSite.data,
          ...action.payload.data,
          menuId: action.payload.data.menu.id,
          hasDelivery:
            (action.payload.data.hasDelivery as unknown as string) === 'true',
          hasPickup:
            (action.payload.data.hasPickup as unknown as string) === 'true',
        } as Oms.MetaDataMenuSite,
      }

      if (action.payload.data) {
        // Only set cookies if the values exist
        if (action.payload.data.customerSessionId) {
          setClientCookie(
            'customerSessionId',
            action.payload.data.customerSessionId,
            {
              path: '/',
              secure: process.env.NODE_ENV !== 'development',
              sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
            }
          )
        }

        if (action.payload.data.locales?.[0]) {
          setClientCookie('lang', action.payload.data.locales[0], {
            path: '/',
            secure: process.env.NODE_ENV !== 'development',
            sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
          })
        }

        if (action.payload.data.storeOrderId) {
          setClientCookie('storeOrderId', action.payload.data.storeOrderId, {
            expires: 7,
            path: '/',
            secure: process.env.NODE_ENV !== 'development',
            sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
          })
        }

        if (action.payload.data.hasPickup !== undefined) {
          setClientCookie(
            'hasPickup',
            action.payload.data.hasPickup.toString(),
            {
              path: '/',
              secure: process.env.NODE_ENV !== 'development',
              sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
            }
          )
        }

        if (action.payload.data.hasDelivery !== undefined) {
          setClientCookie(
            'hasDelivery',
            action.payload.data.hasDelivery.toString(),
            {
              path: '/',
              secure: process.env.NODE_ENV !== 'development',
              sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
            }
          )
        }

        if (action.payload.data.menu?.id) {
          setClientCookie('menuId', action.payload.data.menu.id, {
            path: '/',
            secure: process.env.NODE_ENV !== 'development',
            sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
          })
        }

        if (action.payload.data.marketingAffiliateParams) {
          setClientCookie(
            'marketingAffiliateParams',
            action.payload.data.marketingAffiliateParams,
            {
              path: '/',
              secure: process.env.NODE_ENV !== 'development',
              sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
            }
          )
        }

        if (action.payload.data.SESSION) {
          setClientCookie('SESSION', action.payload.data.SESSION, {
            path: '/',
            secure: process.env.NODE_ENV !== 'development',
            sameSite: process.env.NODE_ENV === 'development' ? 'lax' : 'none',
          })
        }
      }
    },
    setIsBot: (state, action: PayloadAction<boolean>) => {
      state.isBot = action.payload
    },
    setCartName: (state, action: PayloadAction<string>) => {
      state.cartName = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        injectedOmsApi.endpoints.getFeatureToggles.matchFulfilled,
        (state, action) => {
          if (state.detailMenuSite.data) {
            state.detailMenuSite.data.features = action.payload.data
          }
        }
      )
      .addMatcher(
        injectedOmsApi.endpoints.getScripts.matchFulfilled,
        (state, action) => {
          state.scripts.status = 'fulfilled'
          if (action.payload?.payload?.shoppingCartTrackers) {
            state.scripts.data =
              action.payload.payload.shoppingCartTrackers.filter(
                (script) => script.enabled
              )
          }
        }
      )
      .addMatcher(
        injectedOmsApi.endpoints.getTrackers.matchFulfilled,
        (state, action) => {
          state.trackers.status = 'fulfilled'
          state.trackers.data = action.payload?.data
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.getMaxDaysPickerConfig.matchFulfilled,
        (state, action) => {
          if (
            state.detailMenuSite.data &&
            !state.detailMenuSite.data?.features?.enableLimitAdvancedOrders
          ) {
            const maxDaysPickerConfig =
              action.payload.data[action.meta.arg.originalArgs]
            if (typeof maxDaysPickerConfig === 'number') {
              state.detailMenuSite.data.maxDaysPickerConfig =
                maxDaysPickerConfig
            }
          }
        }
      )
  },
})

export const { setListOfMenuSite, setDetailMenuSite, setIsBot, setCartName } =
  metaSlice.actions
